import React, { useCallback, useState } from 'react';
import Styles from './styles';
import ReactHtmlParser from 'react-html-parser';
import IconSvg from '../../../../../../components/common/IconSvg';
import { Avatar, Flex, Popover, Tooltip, Typography } from 'antd';
import { AskMeAnythingMessage } from '../../../../../askMeAnything/types';
import { useMessageReferences } from '../../hooks/useMessageReferences';
import { CalendarOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import { markdownToHtml } from '../../../../../../utils/markdown';
import DOMPurify from 'dompurify';
import { MeetingPopover } from '../../../../../home/tabs/components/MeetingCard/components/MeetingPopover';
import { Event } from '../../../../../home/tabs/components/MeetingCard';
import { fetchRecap } from '../../../../../recapv2/recap-management/service';
import { ParticipantsType } from '../../../../../../API/types';
import { coreService } from '../../../../../../services/core/core-service';
import { useToast } from '../../../../../../hooks/useToast';

const {
  Container,
  PulsingDot,
  MessageContainer,
  Message,
  Timestamp,
  MessageBuble,
  TooltipContent,
  StyledListItem,
  StyledList,
} = Styles;

interface Props {
  fullMessageBubble: boolean;
  messages: AskMeAnythingMessage[];
}

export const MessagesList: React.FC<Props> = ({ messages, fullMessageBubble }) => {
  const { success, error } = useToast();
  const { assignReferencesToMessages } = useMessageReferences();

  const [events, setEvents] = useState<Map<string, Event>>(new Map());

  const handleInsightToggleChanged = useCallback(async (eventId: string, checked: boolean) => {
    try {
      if (checked) {
        await coreService.excludeRecapFromInsights(eventId);
      } else {
        await coreService.includeRecapFromInsights(eventId);
      }

      setEvents(prev => {
        return new Map(prev).set(eventId, {
          ...prev.get(eventId)!,
          excludedFromInsights: checked,
        });
      });

      success('Insight setting updated');
    } catch {
      error('Error while updating insight setting');
    }
  }, []);

  const fetchEvent = async (id: string) => {
    if (events.has(id)) {
      return;
    }

    const data = await fetchRecap(id, null);

    if (!data) {
      return;
    }

    setEvents(prev =>
      new Map(prev).set(id, {
        id: data.id,
        participants: data.participants || [],
        excludedFromInsights: data.excludedFromInsightsEngine,
        participantsType: data.participantsType as ParticipantsType,
        title: data.title,
        scheduledStartDate: format(new Date(data.scheduledStartDate || ''), 'yyyy-MM-dd HH:mm'),
        scheduledEndDate: format(new Date(data.scheduledEndDate || ''), 'yyyy-MM-dd HH:mm'),
        ownerName: 'Owner not identified',
        owner: 'Owner not identified',
        host: data.host,
        account: {
          provider: data.account?.provider || '',
          name: data.account?.name || '',
          id: data.account?.id || '',
          mappingId: '',
        },
        status: data.status,
      }),
    );
  };

  const transformLinks = (
    element: React.ReactElement | React.ReactElement[],
    index: number,
  ): React.ReactNode => {
    if (Array.isArray(element)) {
      return element.map((el, i) => transformLinks(el, i));
    }

    if (!element || typeof element !== 'object') {
      return element;
    }

    if (element.type) {
      if (element.type === 'a') {
        const href = element.props?.href;

        if (href?.includes('/recap/')) {
          const [, partialEventId] = href.split('/recap/');
          const [eventId] = partialEventId.split('?');
          return (
            <Popover
              content={
                events.has(eventId) ? (
                  <MeetingPopover
                    event={events.get(eventId) as Event}
                    onInsightToggleChanged={(checked: boolean) =>
                      handleInsightToggleChanged(eventId, checked)
                    }
                  />
                ) : (
                  <div>Loading...</div>
                )
              }
              arrow={false}
              onOpenChange={() => fetchEvent(eventId)}
            >
              <a {...element.props} target="_blank">
                {element.props.children}
              </a>
            </Popover>
          );
        }

        return (
          <Tooltip
            overlayStyle={{ maxWidth: '600px' }}
            title={
              <TooltipContent>
                <h1>
                  <CalendarOutlined /> {decodeURI(element.props.title)}
                </h1>
                <h3>
                  @ {decodeURI(format(new Date(element.props.date), 'EEE, MMM dd - hh:mm aa'))}
                </h3>
                <h2>{decodeURI(element.props.text)}</h2>
                <h4>
                  <a href={href} target="_blank">
                    Click for more
                  </a>
                </h4>
              </TooltipContent>
            }
            key={index + '-tooltip-anchor-reference'}
          >
            {element}
          </Tooltip>
        );
      }

      if (element.props?.children) {
        const newChildren = transformLinks(element.props.children, index);
        return React.cloneElement(element, { ...element.props, key: index }, newChildren);
      }
    }

    return element;
  };

  return (
    <Container>
      {messages.length === 0 && (
        <Flex
          vertical
          justify="center"
          style={{
            margin: '0 auto',
            maxWidth: fullMessageBubble ? '90%' : '70%',
            height: '100%',
          }}
        >
          <Typography.Text style={{ color: '#000', fontStyle: 'italic' }}>
            UpdateAI’s AI assistant is still in its infancy (i.e. Alpha). Enjoy watching it sprout
            and please share all of your feedback so we can evolve it best to meet your needs (email
            concierge@update.ai).
          </Typography.Text>{' '}
          <Typography.Title level={4} style={{ color: '#000', marginBottom: '4px' }}>
            Important Notes
          </Typography.Title>
          <StyledList>
            <StyledListItem>
              The responses surfaced here are based only on meetings that your account has access
              to.
            </StyledListItem>
            <StyledListItem>
              Your questions and answers here are private and will not be displayed to others in
              your workspace.
            </StyledListItem>
          </StyledList>
          <Typography.Title level={4} style={{ color: '#000', marginBottom: '4px' }}>
            Inspiring Prompts
          </Typography.Title>
          <StyledList style={{ marginTop: '0px' }}>
            <StyledListItem>
              What is the customer sentiment around <strong>[Competitor X]</strong>?
            </StyledListItem>
            <StyledListItem>
              Summarize the most common product feedback requests in the last 90 days.
            </StyledListItem>
            <StyledListItem>
              What CRM does <strong>[insert Prospect name]</strong> use?
            </StyledListItem>
          </StyledList>
        </Flex>
      )}
      {assignReferencesToMessages(messages).map((message, messageIndex) => {
        const isUpdateSender = !message.isSenderUser;
        const content = transformLinks(
          ReactHtmlParser(
            DOMPurify.sanitize(markdownToHtml(message.content), { ADD_ATTR: ['target'] }),
          ),
          0,
        );

        return (
          <MessageContainer
            id={`ama-${messageIndex}`}
            key={messageIndex}
            isUpdateAI={isUpdateSender}
          >
            <Message
              isUpdateAI={isUpdateSender}
              style={{ maxWidth: fullMessageBubble ? '100%' : '70%' }}
            >
              <Avatar
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  backgroundColor: '#FFE6E2',
                  color: '#FF6C54',
                }}
              >
                {isUpdateSender ? (
                  <div style={{ marginTop: '6px' }}>
                    <IconSvg
                      name="updateFull"
                      size="lg"
                      color="transparent"
                      width="24"
                      height="24"
                    />
                  </div>
                ) : (
                  message.sender.slice(0, 1)
                )}
              </Avatar>

              <MessageBuble isUpdateAI={isUpdateSender}>
                {message.isWaiting ? <PulsingDot /> : <div>{content}</div>}
                {!message.isWaiting ? (
                  <Timestamp isUpdateAI={isUpdateSender}>{message.timestamp}</Timestamp>
                ) : (
                  <></>
                )}
              </MessageBuble>
            </Message>
          </MessageContainer>
        );
      })}
    </Container>
  );
};
